import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";

import PatientTabel from "../components/Patient/PatientDetail/PatientTabel";
import AddNewPatientModal from "../components/Modals/AddNewPatient";
import ClinicAdmin from "../components/ClinicAdmin/ClinicAdmin";
import SurveyPoem from "../components/Survey/SurveyPoem";
import Breadcrumbs from "../components/Breadcrumbs";
import NavBar from "../components/navBar/navBar";
import { __API_URL__ } from "../SERVER_URL";
import useUser from "../hooks/user";
import logUserAction from "../apis/userLogger";
import useDeactivateActivatePatientMutation from "../hooks/useDeactivateActivatePatientMutation";
import { USER_ROLES } from "../utils/constants";

const EMPTY_SELECTION = {
  ids: new Set(),
  type: "include",
};

const NewDashboard = () => {
  const user = useUser();
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const [changedPatientTable, setChangedPatientTable] = useState(false);
  const token = localStorage.getItem("token");
  const [clinicData, setClinicData] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });
  const [clinicPatient, setClinicPatient] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState(EMPTY_SELECTION);
  const [typeOfPatient, setTypeOfPatient] = useState("active");
  const [modalShow, setModalShow] = useState(false);
  const [clinicModalShow, setClinicModalShow] = useState(false);
  const [surveyModalShow, setSurveyModalShow] = useState(false);

  const deactivateActivatePatientMutation = useDeactivateActivatePatientMutation();

  const handleDeactivateActivate = (type) => {
    if (rowSelectionModel.ids.size === 0) {
      const messages = {
        active: "Select Subject To Activate!",
        deactive: "Select Subject To Deactivate!",
      };
      toast.warning(messages[type]);
      return;
    }

    // rowSelectionModel.ids are composite `${patientId}-${trialId}` DataGrid row ids (PatientTabel.jsx getRowId)
    const patientIds = Array.from(rowSelectionModel.ids, (id) => String(id).split("-")[0]);

    deactivateActivatePatientMutation.mutate(
      { type, patientIds },
      {
        onSuccess: () => {
          setChangedPatientTable(true);

          const successMessages = {
            active: "Activation was successful!",
            deactive: "Deactivation was successful!",
          };
          toast.success(successMessages[type]);
          setRowSelectionModel(EMPTY_SELECTION);
        },
        onSettled: () => {
          setChangedPatientTable(false);
        },
      }
    );
  };

  const GetClinics = () => {
    axios
      .get(`${__API_URL__}/api/patient/patient/get_clinic_admin/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      });
  };

  const AddClinicPatient = () => {
    if (!clinicData?.email.includes("@")) {
      toast.error("Invalid email.");
    } else {
      const ClinicData = {
        email: clinicData.email,
        confirm_email: clinicData.confirm_email,
        first_name: clinicData.first_name,
        last_name: clinicData.last_name,
        phone: clinicData.phone,
        clinics: clinicData.clinics,
      };
      axios
        .post(`${__API_URL__}/api/patient/patient/add_clinic_patient/`, ClinicData, {
          headers: {
            Authorization: `Token ${token}`,
          },
        })
        .then((res) => {
          setClinicData(res.data);
          setClinicModalShow(false);
          setClinicPatient(true);
          toast.success("Subject Successfully Added");
        })
        .catch((err) => {
          toast.error(err.response.data.message);
        });
    }
  };

  useEffect(() => {
    localStorage.setItem("trial_name_filter", "");
    localStorage.setItem("trial_status_filter", "");
    localStorage.setItem("pass_number", 1);
  }, []);
  const isSubjectRole =
    user_data.role === USER_ROLES.CRO ||
    user_data.role === USER_ROLES.INVESTIGATOR ||
    user_data.role === USER_ROLES.SUB_INVESTIGATOR ||
    user_data.role === USER_ROLES.CRC ||
    role === USER_ROLES.CRO_COORDINATOR ||
    role === USER_ROLES.DATA_MANAGER;

  const showSidebarMenu =
    role !== USER_ROLES.REVIEWER &&
    role !== USER_ROLES.CRO &&
    role !== USER_ROLES.CRO_COORDINATOR &&
    role !== USER_ROLES.DATA_MANAGER;

  // Modal variables
  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="patient" />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            {showSidebarMenu && (
              <Stack direction="row-reverse" gap={1} flexWrap="wrap" width={"100%"}>
                {typeOfPatient === "active" && (
                  <>
                    {role === USER_ROLES.SUPER_ADMIN && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          handleDeactivateActivate("deactive");
                          logUserAction("Click on Deactivate Selected");
                        }}
                      >
                        Deactivate Selected
                      </Button>
                    )}
                    {(role === USER_ROLES.INVESTIGATOR ||
                      role === USER_ROLES.SUB_INVESTIGATOR ||
                      role === USER_ROLES.CRC) && (
                      <Button
                        variant="contained"
                        size="small"
                        data-testid="add-subject-btn"
                        onClick={() => {
                          logUserAction("Click on Add New Subject");
                          setModalShow(true);
                        }}
                      >
                        Add New Subject
                      </Button>
                    )}
                    {(role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.CLINIC_ADMIN) && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setClinicModalShow(true);
                          logUserAction("Click on Add Clinic Subject");
                        }}
                      >
                        Add Clinic Subject
                      </Button>
                    )}
                  </>
                )}
                {typeOfPatient === "deactive" && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleDeactivateActivate("active")}
                  >
                    Activate Selected
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <PatientTabel
              typeOfPatient={typeOfPatient}
              setRowSelectionModel={setRowSelectionModel}
              rowSelectionModel={rowSelectionModel}
              changedPatientTable={changedPatientTable}
              setChangedPatientTable={setChangedPatientTable}
              clinicPatient={clinicPatient}
            />
          </Box>
        </Box>
      </Box>

      {modalShow && <AddNewPatientModal modalShow={setModalShow} />}
      {clinicModalShow && (
        <ClinicAdmin
          show={clinicModalShow}
          onHide={() => setClinicModalShow(false)}
          clinicModalShow={setClinicModalShow}
          AddClinicPatient={AddClinicPatient}
          GetClinics={GetClinics}
          setClinics={setClinics}
          clinics={clinics}
          clinicData={clinicData}
          setClinicData={setClinicData}
          clinicPatient={clinicPatient}
        />
      )}
      {surveyModalShow && (
        <SurveyPoem
          show={surveyModalShow}
          setSurveyModalShow={setSurveyModalShow}
          onHide={() => setSurveyModalShow(false)}
        />
      )}
    </Box>
  );
};

export default NewDashboard;
